import React, { FunctionComponent, useEffect, useState, useCallback } from "react";
import { Invoice, Claim } from "../../../../types";
import { getStatusBadgeColor } from "../../../utils";
import "../DetailView.css";
import ErrorAlert from "../../../components/error-alert/ErrorAlert";
import RetryButton from "../../../components/error-alert/RetryButton";
import { downloadFileFromBackend } from "../../../util/download";
import { useSelector } from "react-redux";
import { State } from "../../../../store/state";
import { getFileIcon } from "../../../components/icon/Icon";

type InvoicesProps = {
  dataSelected: Claim;
  invoices: Invoice[];
  requestGetInvoicesByClaimId: (id: number) => Promise<any>;
};
const Invoices: FunctionComponent<InvoicesProps> = (props) => {
  const accessToken = useSelector((state: State) => state.authentication.accessToken);
  const { dataSelected, invoices, requestGetInvoicesByClaimId } = props;
  const [showError, setShowError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showMore, setShowMore] = useState<string[]>([]);

  const loadInvoices = useCallback(() => {
    setIsFetching(true);
    setShowError(false);
    requestGetInvoicesByClaimId(dataSelected.id)
      .catch(() => setShowError(true))
      .finally(() => setIsFetching(false));
  }, [requestGetInvoicesByClaimId, dataSelected]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const formatCurrency = (value: number) => {
    const formatter = new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: "NZD",
      minimumFractionDigits: 0,
    });

    return formatter.format(value);
  };

  const manageExpandView = (invoiceNumber: string) => {
    if (showMore.includes(invoiceNumber)) {
      setShowMore(showMore.filter((item) => item !== invoiceNumber));
    } else {
      setShowMore([...showMore, invoiceNumber]);
    }
  };

  const getAttachmentName = (attachment: string) => {
    const fileNameComplete = attachment.substring(attachment.lastIndexOf("/") + 1);
    const fileExtension = attachment.substring(attachment.lastIndexOf(".") + 1);
    const fileName = fileNameComplete.substring(0, fileNameComplete.lastIndexOf("."));

    if (fileName.length > 8) {
      return {
        fileNameComplete,
        fileName: `${fileName.substring(0, 4)}...${fileName.substring(
          fileName.length - 4
        )}.${fileExtension}`,
      };
    }

    return { fileNameComplete, fileName: fileNameComplete };
  };

  const renderAttachments = (invoice: Invoice) => {
    if (!invoice.attachments || invoice.attachments.length === 0) {
      return null;
    }

    let expandView = invoice.attachments.length > 1;

    if (!expandView) {
      const fileNames = getAttachmentName(invoice.attachments[0]);
      return (
        <div
          key={`${invoice.invoiceNumber}`}
          title={fileNames.fileNameComplete}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            fontSize: "0.8rem",
          }}
          onClick={() => {
            downloadFileFromBackend(invoice.attachments[0], accessToken);
          }}>
          {getFileIcon(invoice.attachments[0])}
          {fileNames.fileName}
        </div>
      );
    }

    return (
      <div
        key={`${invoice.invoiceNumber}`}
        style={{
          display: "flex",
          justifyContent: "center",
        }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary text-nowrap"
          style={{ fontSize: "0.8rem", lineHeight: 1 }}
          onClick={() => manageExpandView(invoice.invoiceNumber)}>
          Show more
          {showMore.includes(invoice.invoiceNumber) ? (
            <i
              className="fas fa-chevron-up"
              style={{
                cursor: "pointer",
                color: "#6c757d",
                paddingLeft: "0.3rem",
              }}
            />
          ) : (
            <i
              className="fas fa-chevron-down"
              style={{
                cursor: "pointer",
                color: "#6c757d",
                paddingLeft: "0.3rem",
              }}
            />
          )}
        </button>
      </div>
    );
  };

  const renderMultiAttachments = (invoice: Invoice) => {
    return invoice.attachments.map((attachment: string, _index) => {
      const fileNames = getAttachmentName(attachment);
      return (
        <div
          key={`${invoice.invoiceNumber} ${_index}`}
          title={fileNames.fileNameComplete}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            fontSize: "0.8rem",
            padding: "0.5rem",
            flex: "0 0 20%",
          }}
          onClick={() => {
            downloadFileFromBackend(attachment, accessToken);
          }}>
          {getFileIcon(attachment)}
          {fileNames.fileName}
        </div>
      );
    });
  };

  const getInvoices = () => {
    if (invoices.length === 0 || !invoices) {
      return <div>Currently no invoices.</div>;
    }

    return (
      <table className="table table-striped">
        <thead>
          <tr>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Number
            </th>
            <th className="td-style" style={{ padding: "0.35rem", textAlign: "center" }}>
              Status
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Total
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Total incl. GST
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Supplier
            </th>
            <th className="td-style" style={{ padding: "0.35rem", textAlign: "center" }}>
              Attachments
            </th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice: Invoice, __index) => {
            let color = "#fff";
            if (__index % 2 === 0) {
              color = "rgba(0, 0, 0, 0.05)";
            }
            return (
              <>
                <tr key={invoice.id} style={{ backgroundColor: color }}>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div>{invoice.invoiceNumber}</div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <span
                        className="badge badge-pill"
                        style={{
                          padding: "0.25rem 0.5rem",
                          fontWeight: 500,
                          fontSize: "11px",
                          color: getStatusBadgeColor(invoice.status).text,
                          backgroundColor: getStatusBadgeColor(invoice.status).background,
                        }}>
                        {invoice.status}
                      </span>
                    </div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div>{formatCurrency(invoice.price)}</div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div>{formatCurrency(invoice.priceAfterTax)}</div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div>{invoice.supplier}</div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    {renderAttachments(invoice)}
                  </td>
                </tr>
                {showMore.includes(invoice.invoiceNumber) && (
                  <tr style={{ backgroundColor: color }}>
                    <td colSpan={6}>
                      <div
                        style={{
                          display: "flex",
                          flex: "1 1 auto",
                          flexWrap: "wrap",
                        }}>
                        {renderMultiAttachments(invoice)}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="detail-wrapper">
      <div className="heading">Invoices</div>
      {isFetching && (
        <div
          className="d-flex justify-content-center"
          style={{
            paddingTop: "1rem",
          }}>
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
      {showError && !isFetching && (
        <div
          style={{
            display: "flex",
            flex: "0 0 auto",
            paddingTop: "1rem",
            justifyContent: "center",
          }}>
          <ErrorAlert lightMode>
            <span style={{ paddingRight: "1rem" }}>
              Error while loading the invoices of the selected claim. Please retry or submit a
              Support Desk ticket.
            </span>
            <RetryButton retryFunction={loadInvoices} />
          </ErrorAlert>
        </div>
      )}
      {!showError && !isFetching && getInvoices()}
    </div>
  );
};

export default Invoices;
